import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { reviewsAPI } from '../lib/api';
import { StarRating } from '../components/ReviewsSection';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Loader2,
  Check,
  X,
  Trash2,
  MessageSquare,
  ShieldCheck,
  Store,
  Package,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'approved', label: 'Aprovadas' },
  { key: 'rejected', label: 'Reprovadas' },
  { key: '', label: 'Todas' },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const Reviews = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [targetType, setTargetType] = useState('');
  const [rejectDialog, setRejectDialog] = useState({ open: false, review: null, notes: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, review: null });
  const [actioning, setActioning] = useState(null); // id sendo processado

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await reviewsAPI.adminList({
        ...(status ? { status } : {}),
        ...(targetType ? { target_type: targetType } : {}),
      });
      setItems(data.items || []);
      setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status, targetType]);

  const approve = async (r) => {
    setActioning(r.id);
    try {
      await reviewsAPI.moderate(r.id, { status: 'approved' });
      toast.success('Avaliação aprovada — já aparece publicamente!');
      load();
    } catch (e) {
      toast.error('Erro ao aprovar');
    } finally {
      setActioning(null);
    }
  };

  const reject = async () => {
    const r = rejectDialog.review;
    if (!r) return;
    setActioning(r.id);
    try {
      await reviewsAPI.moderate(r.id, { status: 'rejected', admin_notes: rejectDialog.notes });
      toast.success('Avaliação reprovada');
      setRejectDialog({ open: false, review: null, notes: '' });
      load();
    } catch (e) {
      toast.error('Erro ao reprovar');
    } finally {
      setActioning(null);
    }
  };

  const remove = async () => {
    const r = deleteDialog.review;
    if (!r) return;
    setActioning(r.id);
    try {
      await reviewsAPI.delete(r.id);
      toast.success('Avaliação excluída');
      setDeleteDialog({ open: false, review: null });
      load();
    } catch (e) {
      toast.error('Erro ao excluir');
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reviews-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-blue-600" />
            Moderação de Avaliações
          </h1>
          <p className="text-slate-500 mt-1">Aprove, reprove ou exclua avaliações enviadas pelos clientes.</p>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold" data-testid="count-pending">
            {counts.pending} pendente{counts.pending === 1 ? '' : 's'}
          </span>
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold" data-testid="count-approved">
            {counts.approved} aprovada{counts.approved === 1 ? '' : 's'}
          </span>
          <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-semibold" data-testid="count-rejected">
            {counts.rejected} reprovada{counts.rejected === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Status
            </p>
            <div className="flex flex-wrap gap-2" data-testid="status-tabs">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key || 'all'}
                  onClick={() => setStatus(t.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    status === t.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`tab-status-${t.key || 'all'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Tipo</p>
            <div className="flex gap-2" data-testid="type-tabs">
              {[
                { key: '', label: 'Todos' },
                { key: 'product', label: 'Produto', icon: Package },
                { key: 'store', label: 'Loja', icon: Store },
              ].map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key || 'all'}
                    onClick={() => setTargetType(t.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                      targetType === t.key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`tab-type-${t.key || 'all'}`}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-slate-500" data-testid="reviews-empty">
          Nenhuma avaliação encontrada para esse filtro.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {items.map((r) => (
            <Card key={r.id} className="border-slate-200" data-testid={`admin-review-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                        r.target_type === 'product'
                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                          : 'bg-purple-50 text-purple-700 border border-purple-200'
                      }`}>
                        {r.target_type === 'product' ? <Package className="w-3 h-3" /> : <Store className="w-3 h-3" />}
                        {r.target_type === 'product' ? 'Produto' : 'Loja'}
                      </span>
                      <Link
                        to={r.target_type === 'product' ? `/produto/${r.target_id}` : `/loja/${r.target_id}/institucional`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-slate-900 hover:text-blue-700 truncate"
                        data-testid={`review-target-link-${r.id}`}
                      >
                        {r.target_name || '(alvo removido)'}
                      </Link>
                      {r.verified_purchase && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                          <ShieldCheck className="w-3 h-3" /> Compra verificada
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <StarRating value={r.rating} size={14} />
                      <span className="text-xs text-slate-500">
                        {r.customer_name} • {formatDate(r.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{r.comment}</p>
                    {r.admin_notes && (
                      <p className="mt-2 text-xs italic text-slate-500">Nota do admin: {r.admin_notes}</p>
                    )}
                    {r.order_protocol && (
                      <p className="mt-1 text-[11px] text-slate-400 font-mono">Pedido: {r.order_protocol}</p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[140px]">
                    {r.status !== 'approved' && (
                      <Button
                        size="sm"
                        onClick={() => approve(r)}
                        disabled={actioning === r.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`approve-btn-${r.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Aprovar
                      </Button>
                    )}
                    {r.status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectDialog({ open: true, review: r, notes: r.admin_notes || '' })}
                        disabled={actioning === r.id}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`reject-btn-${r.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Reprovar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteDialog({ open: true, review: r })}
                      disabled={actioning === r.id}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-btn-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog((s) => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprovar avaliação</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Justificativa (opcional, visível apenas para o administrador):
          </p>
          <Textarea
            value={rejectDialog.notes}
            onChange={(e) => setRejectDialog((s) => ({ ...s, notes: e.target.value }))}
            placeholder="Ex.: linguagem imprópria, conteúdo repetido, etc."
            rows={3}
            data-testid="reject-notes-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, review: null, notes: '' })}>
              Cancelar
            </Button>
            <Button
              onClick={reject}
              disabled={actioning === rejectDialog.review?.id}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reject-btn"
            >
              Confirmar reprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => setDeleteDialog((s) => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir avaliação</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Essa ação é permanente. Tem certeza que deseja excluir a avaliação de{' '}
            <strong>{deleteDialog.review?.customer_name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, review: null })}>
              Cancelar
            </Button>
            <Button
              onClick={remove}
              disabled={actioning === deleteDialog.review?.id}
              variant="destructive"
              data-testid="confirm-delete-review-btn"
            >
              Excluir definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reviews;
